import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import useDialogFocus from "@/lib/useDialogFocus";
import { AnimatePresence, motion } from "framer-motion";
import { X, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const MediaCtx = createContext({ open: () => {}, close: () => {} });
export const useMedia = () => useContext(MediaCtx);

export function MediaProvider({ children }) {
  const [gallery, setGallery] = useState(null);
  const [index, setIndex] = useState(0);

  const open = useCallback((m) => {
    if (m && Array.isArray(m.items)) {
      setGallery(m.items);
      setIndex(m.index || 0);
    } else {
      setGallery([m]);
      setIndex(0);
    }
  }, []);
  const close = useCallback(() => setGallery(null), []);
  const dialog = useRef(null);
  useDialogFocus(Boolean(gallery), dialog);

  const count = gallery ? gallery.length : 0;
  const item = gallery ? gallery[index] : null;
  const next = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      setIndex((i) => (i + 1) % count);
    },
    [count]
  );
  const prev = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      setIndex((i) => (i - 1 + count) % count);
    },
    [count]
  );

  useEffect(() => {
    if (!gallery) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight" && count > 1) next();
      else if (e.key === "ArrowLeft" && count > 1) prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gallery, count, next, prev, close]);

  const stop = (e) => e.stopPropagation();

  return (
    <MediaCtx.Provider value={{ open, close }}>
      {children}
      <AnimatePresence>
        {item && (
          <motion.div
            data-testid="media-viewer"
            ref={dialog}
            role="dialog"
            aria-modal="true"
            aria-label={count > 1 ? `Media viewer, item ${index + 1} of ${count}` : "Media viewer"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={close}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/92 p-5 backdrop-blur-md md:p-14"
          >
            <button
              data-testid="media-viewer-close"
              onClick={close}
              aria-label="Close viewer"
              className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-paper/90 text-ink transition-transform duration-300 hover:scale-105 md:right-8 md:top-8"
            >
              <X className="h-5 w-5" />
            </button>

            <a
              href={item.src}
              target="_blank"
              rel="noreferrer"
              onClick={stop}
              data-testid="media-viewer-newtab"
              className="absolute left-5 top-5 z-10 flex items-center gap-2 rounded-full bg-paper/90 px-4 py-2.5 text-ink transition-transform duration-300 hover:scale-105 md:left-8 md:top-8"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              <span className="overline text-[0.6rem]">Open in new window</span>
              <span className="sr-only">(opens a new tab)</span>
            </a>

            {count > 1 && (
              <>
                <button
                  data-testid="media-viewer-prev"
                  onClick={prev}
                  aria-label="Previous item"
                  className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-paper/90 text-ink transition-transform duration-300 hover:scale-105 md:left-8"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  data-testid="media-viewer-next"
                  onClick={next}
                  aria-label="Next item"
                  className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-paper/90 text-ink transition-transform duration-300 hover:scale-105 md:right-8"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}

            {item.type === "video" ? (
              <motion.video
                key={item.src}
                initial={{ scale: 0.97, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.45, ease: EASE }}
                src={item.src}
                poster={item.poster}
                controls
                autoPlay
                loop
                playsInline
                onClick={stop}
                className="max-h-full max-w-full object-contain shadow-2xl"
              />
            ) : (
              <motion.img
                key={item.src}
                initial={{ scale: 0.97, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.45, ease: EASE }}
                src={item.src}
                alt={item.alt || ""}
                onClick={stop}
                className="max-h-full max-w-full object-contain shadow-2xl"
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </MediaCtx.Provider>
  );
}

// A clickable still image that opens in the shared media viewer.
export function OpenableImage({ src, alt, className = "", label = "Still", imgClass = "" }) {
  const { open } = useMedia();
  return (
    <button
      type="button"
      data-cursor-hover
      onClick={() => open({ type: "image", src, alt })}
      className={`group relative block w-full overflow-hidden bg-paper2 text-left ${className}`}
    >
      <img src={src} alt={alt} className={`hover-media w-full object-cover ${imgClass}`} />
    </button>
  );
}
